import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Badge } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useMessages } from '../../contexts/MessageContext';

const DARK = 'rgb(28,37,54)';
const ACCENT = '#ffcc00';

const useStyles = makeStyles(() => ({
  shell: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    overflow: 'hidden',
    background: '#fff',
  },

  /* ── TOP NAV ── */
  topNav: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '10px 16px',
    background: DARK,
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    flexShrink: 0,
    flexWrap: 'wrap',
    rowGap: 8,
  },
  title: {
    color: '#fff',
    fontWeight: 700,
    fontSize: 15,
    letterSpacing: '0.01em',
    marginRight: 8,
    flexShrink: 0,
  },
  tabBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 6,
    border: 'none',
    background: 'transparent',
    color: '#9ca3af',
    fontSize: 13.5,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.12s, color 0.12s',
    flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.07)', color: '#e5e7eb' },
  },
  tabBtnActive: {
    background: 'rgba(255,204,0,0.15) !important',
    color: `${ACCENT} !important`,
  },
  divider: {
    width: 1,
    height: 20,
    background: 'rgba(255,255,255,0.1)',
    margin: '0 6px',
    flexShrink: 0,
  },
  spacer: { flex: 1 },
  composeBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    padding: '7px 16px',
    borderRadius: 6,
    border: 'none',
    background: ACCENT,
    color: '#1a1a1a',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'opacity 0.15s',
    '&:hover': { opacity: 0.88 },
  },

  /* ── CONTENT ── */
  content: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function MessagesLayout({ children }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { unreadCount } = useMessages();

  const isConversation = location.pathname.startsWith('/messages/conversation');
  const isCompose      = location.pathname === '/messages/compose';
  const isInbox        = location.pathname === '/messages/inbox' || location.pathname === '/messages';
  const isSent         = location.pathname === '/messages/sent';

  return (
    <div className={classes.shell}>
      {/* ── TOP NAV ── */}
      <div className={classes.topNav}>
        <span className={classes.title}>
          <i className="fa-solid fa-envelope" style={{ marginRight: 8, opacity: 0.7 }} />
          Messages
        </span>

        <div className={classes.divider} />

        {/* Inbox tab */}
        <button
          className={`${classes.tabBtn} ${isInbox || isConversation ? classes.tabBtnActive : ''}`}
          onClick={() => history.push('/messages/inbox')}
        >
          <Badge badgeContent={unreadCount || 0} color="error" max={99}>
            <i className="fa-solid fa-inbox" style={{ fontSize: 14 }} />
          </Badge>
          <span>Inbox</span>
        </button>

        {/* Sent tab */}
        <button
          className={`${classes.tabBtn} ${isSent ? classes.tabBtnActive : ''}`}
          onClick={() => history.push('/messages/sent')}
        >
          <i className="fa-solid fa-paper-plane" style={{ fontSize: 14 }} />
          <span>Sent</span>
        </button>

        <div className={classes.spacer} />

        {/* Compose button */}
        {!isCompose && (
          <button className={classes.composeBtn} onClick={() => history.push('/messages/compose')}>
            <i className="fa-solid fa-pen-to-square" />
            <span>New Message</span>
          </button>
        )}
      </div>

      {/* ── PAGE CONTENT ── */}
      <div className={classes.content}>{children}</div>
    </div>
  );
}
